// src/features/automacaodeemails/pages/AutomationWizard.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../api";

const ICONS = {
  arrowLeft: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12,19 5,12 12,5" />
    </svg>
  ),
  arrowRight: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12,5 19,12 12,19" />
    </svg>
  ),
  check: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="20,6 9,17 4,12" />
    </svg>
  ),
  scan: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  ),
  table: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <line x1="3" y1="9" x2="21" y2="9" />
      <line x1="3" y1="15" x2="21" y2="15" />
      <line x1="9" y1="3" x2="9" y2="21" />
      <line x1="15" y1="3" x2="15" y2="21" />
    </svg>
  ),
  chart: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  ),
  cards: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  ),
  loader: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="ae2-spin">
      <line x1="12" y1="2" x2="12" y2="6" />
      <line x1="12" y1="18" x2="12" y2="22" />
      <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" />
      <line x1="16.24" y1="16.24" x2="19.07" y2="19.07" />
      <line x1="2" y1="12" x2="6" y2="12" />
      <line x1="18" y1="12" x2="22" y2="12" />
    </svg>
  ),
  x: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
};

const TIPO_ICONS = { table: ICONS.table, chart: ICONS.chart, cards: ICONS.cards };
const FREQ_OPTIONS = [
  { value: "DAILY", label: "Diario" },
  { value: "WEEKLY", label: "Semanal" },
  { value: "MONTHLY", label: "Mensal" },
];
const DAYS_OPTIONS = [
  { value: "mon", label: "Seg" },
  { value: "tue", label: "Ter" },
  { value: "wed", label: "Qua" },
  { value: "thu", label: "Qui" },
  { value: "fri", label: "Sex" },
  { value: "sat", label: "Sab" },
  { value: "sun", label: "Dom" },
];

function parseMetadata(meta) {
  if (!meta) return null;
  if (typeof meta === "object") return meta;
  try {
    return JSON.parse(meta);
  } catch {
    return null;
  }
}

function toNumber(val) {
  const num = Number(val);
  return Number.isFinite(num) ? num : null;
}

function getExcludeSelector(el) {
  if (!el) return "";
  const meta = el._meta || el.metadata || null;
  return (
    meta?.capture_selector ||
    meta?.table_selector ||
    el.capture_selector ||
    el.table_selector ||
    el.selector ||
    ""
  );
}

function getEmailGroups(email) {
  if (!email) return [];
  const raw = Array.isArray(email.grupos) && email.grupos.length
    ? email.grupos
    : (email.grupo ? String(email.grupo).split(",") : []);
  const seen = new Set();
  const out = [];
  for (const item of raw) {
    const name = String(item || "").trim();
    if (!name) continue;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(name);
  }
  return out;
}

export default function AutomationWizard() {
  const nav = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(null);
  const lastAutoScanRef = useRef(null);

  const [screens, setScreens] = useState([]);
  const [emailsBase, setEmailsBase] = useState([]);
  const [recipientSearch, setRecipientSearch] = useState("");
  const [recipientGroup, setRecipientGroup] = useState("all");

  const [selectedElements, setSelectedElements] = useState([]);
  const [screensAll, setScreensAll] = useState([]);
  const [screensFull, setScreensFull] = useState([]);
  const [screensFullExcludes, setScreensFullExcludes] = useState({});
  const [activeScreenId, setActiveScreenId] = useState(null);
  const [screenSearch, setScreenSearch] = useState("");
  const [elementSearch, setElementSearch] = useState("");
  const [elementType, setElementType] = useState("all");
  const [elementSort, setElementSort] = useState("confidence");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [previewScreens, setPreviewScreens] = useState({});
  const [pickerError, setPickerError] = useState("");
  const [pickerImgUrl, setPickerImgUrl] = useState("");
  const [pickerDims, setPickerDims] = useState({
    naturalWidth: 0,
    naturalHeight: 0,
    renderWidth: 0,
    renderHeight: 0,
    scale: 1,
  });
  const [triggerCatalog, setTriggerCatalog] = useState([]);
  const [selectedTriggers, setSelectedTriggers] = useState([]);
  const [assets, setAssets] = useState([]);
  const [signatureTab, setSignatureTab] = useState("assinatura");
  const [form, setForm] = useState({
    nome: "",
    assunto: "",
    corpo_md: "",
    destinatarios_ids: [],
    assinatura_md: "",
    assinatura_img_base64: "",
    assinatura_img_mime: "",
    assinatura_img_nome: "",
    assinatura_extra_img_base64: "",
    assinatura_extra_img_mime: "",
    assinatura_extra_img_nome: "",
    assinatura_ativa: true,
    freq: "DAILY",
    hora: "09:00",
    dias_semana: ["mon"],
    dia_mes: 1,
    attach_pdf: true,
  });

  const selectedSet = useMemo(() => new Set(selectedElements), [selectedElements]);
  const screensAllSet = useMemo(() => new Set(screensAll), [screensAll]);
  const screensFullSet = useMemo(() => new Set(screensFull), [screensFull]);
  const pickerImgRef = useRef(null);
  const previewScreenUrlsRef = useRef(new Set());

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [screensRes, emailsRes, triggersRes, assetsRes] = await Promise.all([
        api.screens.list({ include_elements: 1 }),
        api.emailsBase.list(),
        api.triggers.catalog(),
        api.assets.list(),
      ]);
      setScreens(screensRes?.data || []);
      setEmailsBase(emailsRes || []);
      setTriggerCatalog(triggersRes?.data || []);
      setAssets(assetsRes?.data || assetsRes || []);

      if (id) {
        const autoRes = await api.automations.get(id);
        const auto = autoRes?.data;
        if (auto) {
          setForm({
            nome: auto.nome || "",
            assunto: auto.assunto || "",
            corpo_md: auto.corpo_md || "",
            destinatarios_ids: auto.destinatarios?.map((d) => d.id) || [],
            assinatura_md: auto.assinatura_md || "",
            assinatura_img_base64: auto.assinatura_img_base64 || "",
            assinatura_img_mime: auto.assinatura_img_mime || "",
            assinatura_img_nome: auto.assinatura_img_nome || "",
            assinatura_extra_img_base64: auto.assinatura_extra_img_base64 || "",
            assinatura_extra_img_mime: auto.assinatura_extra_img_mime || "",
            assinatura_extra_img_nome: auto.assinatura_extra_img_nome || "",
            assinatura_ativa: auto.assinatura_ativa !== false,
            freq: auto.freq || "DAILY",
            hora: auto.hora || "09:00",
            dias_semana: auto.dias_semana?.split(",") || ["mon"],
            dia_mes: auto.dia_mes || 1,
            attach_pdf: auto.attach_pdf !== false,
          });
          setSelectedElements(auto.elements?.map((e) => e.element_id) || []);
          setScreensAll(auto.screens_all_ids || []);
          setScreensFull(auto.screens_full_ids || []);
          setScreensFullExcludes(auto.screens_full_excludes || {});
          setSelectedTriggers(
            (auto.triggers || []).map((t) => ({
              type: t.trigger_type || t.type,
              key: t.trigger_key || t.key,
              label: t.trigger_label || t.label,
            }))
          );
        }
      }
    } catch (err) {
      console.error("Erro ao carregar dados:", err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (screens.length === 0) {
      if (activeScreenId !== null) setActiveScreenId(null);
      return;
    }
    if (activeScreenId === null) return;
    const exists = screens.some((s) => s.id === activeScreenId);
    if (!exists) setActiveScreenId(null);
  }, [screens, activeScreenId]);

  useEffect(() => {
    if (!activeScreenId) return;
    const screen = screens.find((s) => s.id === activeScreenId);
    if (!screen) return;

    setScreensFull((prev) => (prev.includes(activeScreenId) ? prev : [...prev, activeScreenId]));
    setScreensAll((prev) => prev.filter((id) => id !== activeScreenId));

    const ids = (screen.elements || []).map((e) => e.id);
    if (ids.length > 0) {
      setSelectedElements((prev) => prev.filter((id) => !ids.includes(id)));
    }
  }, [activeScreenId, screens]);


  useEffect(() => {
    if (!activeScreenId) return;
    if (scanning) return;
    if (lastAutoScanRef.current === activeScreenId) return;
    lastAutoScanRef.current = activeScreenId;
    handleScan(activeScreenId);
  }, [activeScreenId, scanning]);

  async function handleScan(screenId) {
    setScanning(screenId);
    try {
      await api.screens.scan(screenId);
      const res = await api.screens.list({ include_elements: 1 });
      setScreens(res?.data || []);
    } catch (err) {
      alert(`Erro ao escanear: ${err?.message}`);
    } finally {
      setScanning(null);
    }
  }

  function toggleElement(elementId) {
    if (screensFullSet.has(activeScreenId)) {
      const el = elementById.get(elementId);
      if (!el) return;
      const selector = getExcludeSelector(el);
      if (!selector) return;
      setScreensFullExcludes((prev) => {
        const next = { ...(prev || {}) };
        const sid = el.screen_id;
        const current = new Set(Array.isArray(next[sid]) ? next[sid] : []);
        if (current.has(selector)) {
          current.delete(selector);
        } else {
          current.add(selector);
        }
        next[sid] = Array.from(current);
        return next;
      });
      return;
    }
    setSelectedElements((prev) =>
      prev.includes(elementId) ? prev.filter((id) => id !== elementId) : [...prev, elementId]
    );
  }

  function toggleScreenAll(screen) {
    const sid = screen?.id;
    if (!sid) return;
    setScreensAll((prev) => {
      const exists = prev.includes(sid);
      if (exists) return prev.filter((id) => id !== sid);
      return [...prev, sid];
    });

    if (!screensAllSet.has(sid)) {
      setScreensFull((prev) => prev.filter((id) => id !== sid));
    }

    if (!screensAllSet.has(sid)) {
      const ids = (screen.elements || []).filter((e) => e.is_valid).map((e) => e.id);
      if (ids.length > 0) {
        setSelectedElements((prev) => Array.from(new Set([...prev, ...ids])));
      }
    }
  }

  function toggleScreenFull(screen) {
    const sid = screen?.id;
    if (!sid) return;
    const wasFull = screensFullSet.has(sid);
    setScreensFull((prev) => {
      if (prev.includes(sid)) return prev.filter((id) => id !== sid);
      return [...prev, sid];
    });

    if (wasFull) {
      setScreensFullExcludes((prev) => {
        if (!prev || !prev[sid]) return prev;
        const next = { ...prev };
        delete next[sid];
        return next;
      });
    }

    if (!wasFull) {
      setScreensAll((prev) => prev.filter((id) => id !== sid));
      const ids = (screen.elements || []).map((e) => e.id);
      if (ids.length > 0) {
        setSelectedElements((prev) => prev.filter((id) => !ids.includes(id)));
      }
    }
  }

  function toggleDestinatario(emailId) {
    setForm((prev) => ({
      ...prev,
      destinatarios_ids: prev.destinatarios_ids.includes(emailId)
        ? prev.destinatarios_ids.filter((id) => id !== emailId)
        : [...prev.destinatarios_ids, emailId],
    }));
  }

  function toggleDiaSemana(day) {
    setForm((prev) => ({
      ...prev,
      dias_semana: prev.dias_semana.includes(day)
        ? prev.dias_semana.filter((d) => d !== day)
        : [...prev.dias_semana, day],
    }));
  }

  function toggleTrigger(item) {
    const key = item?.key || item?.trigger_key;
    const type = item?.type || item?.trigger_type;
    if (!key || !type) return;
    setSelectedTriggers((prev) => {
      const exists = prev.some((t) => t.key === key && t.type === type);
      if (exists) return prev.filter((t) => !(t.key === key && t.type === type));
      return [...prev, { type, key, label: item.label }];
    });
  }

  const filteredScreens = useMemo(() => {
    const q = screenSearch.trim().toLowerCase();
    if (!q) return screens;
    return screens.filter((s) =>
      String(s.nome || "").toLowerCase().includes(q) ||
      String(s.url || "").toLowerCase().includes(q)
    );
  }, [screens, screenSearch]);

  const recipientGroups = useMemo(() => {
    const groups = new Set();
    for (const email of emailsBase) {
      for (const grupo of getEmailGroups(email)) groups.add(grupo);
    }
    return Array.from(groups).sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [emailsBase]);

  const filteredRecipients = useMemo(() => {
    const q = recipientSearch.trim().toLowerCase();
    return emailsBase.filter((email) => {
      const grupos = getEmailGroups(email);
      if (recipientGroup !== "all" && !grupos.includes(recipientGroup)) return false;
      if (!q) return true;
      return [email?.nome, email?.email, grupos.join(", ")]
        .some((v) => String(v || "").toLowerCase().includes(q));
    });
  }, [emailsBase, recipientGroup, recipientSearch]);

  const activeScreen = useMemo(() => {
    return screens.find((s) => s.id === activeScreenId) || screens[0] || null;
  }, [screens, activeScreenId]);

  const activeElementsRaw = useMemo(() => {
    return (activeScreen?.elements || []).filter((e) => e.is_valid);
  }, [activeScreen]);

  const activeElementsWithMeta = useMemo(() => {
    return activeElementsRaw.map((el) => ({
      ...el,
      _meta: parseMetadata(el.metadata),
    }));
  }, [activeElementsRaw]);

  const activeElements = useMemo(() => {
    const byType = elementType === "all"
      ? activeElementsRaw
      : activeElementsRaw.filter((e) => e.tipo === elementType);
    const q = elementSearch.trim().toLowerCase();
    const bySearch = q
      ? byType.filter((e) =>
          String(e.auto_name || "").toLowerCase().includes(q) ||
          String(e.preview || "").toLowerCase().includes(q) ||
          String(e.selector || "").toLowerCase().includes(q)
        )
      : byType;
    const sorted = [...bySearch];
    if (elementSort === "name") {
      sorted.sort((a, b) => String(a.auto_name || "").localeCompare(String(b.auto_name || "")));
    } else if (elementSort === "recent") {
      sorted.sort((a, b) => new Date(b.detected_at || 0) - new Date(a.detected_at || 0));
    } else {
      sorted.sort((a, b) => (b.confidence || 0) - (a.confidence || 0));
    }
    return sorted;
  }, [activeElementsRaw, elementType, elementSearch, elementSort]);

  const pickerElements = useMemo(() => {
    return activeElementsWithMeta.filter((el) => {
      const rect = el._meta?.rect;
      if (!rect) return false;
      const width = toNumber(rect.width);
      const height = toNumber(rect.height);
      const x = toNumber(rect.x ?? rect.left);
      const y = toNumber(rect.y ?? rect.top);
      return width && height && x !== null && y !== null;
    });
  }, [activeElementsWithMeta]);

  const screensFullExcludeSets = useMemo(() => {
    const map = new Map();
    for (const [key, list] of Object.entries(screensFullExcludes || {})) {
      const sid = Number(key);
      if (!Number.isFinite(sid)) continue;
      map.set(sid, new Set(Array.isArray(list) ? list : []));
    }
    return map;
  }, [screensFullExcludes]);

  const activeFullExcludeSet = useMemo(() => {
    return screensFullExcludeSets.get(activeScreenId) || new Set();
  }, [screensFullExcludeSets, activeScreenId]);

  const pickerSelected = useMemo(() => {
    if (screensFullSet.has(activeScreenId)) {
      return pickerElements.filter((el) => activeFullExcludeSet.has(getExcludeSelector(el)));
    }
    return pickerElements.filter((el) => selectedSet.has(el.id));
  }, [pickerElements, selectedSet, screensFullSet, activeScreenId, activeFullExcludeSet]);

  const allElements = useMemo(() => {
    const elements = [];
    for (const screen of screens) {
      if (screen.elements) {
        for (const el of screen.elements) {
          if (el.is_valid) {
            elements.push({ ...el, screen_nome: screen.nome, screen_url: screen.url, screen_id: screen.id });
          }
        }
      }
    }
    return elements;
  }, [screens]);

  const elementById = useMemo(() => {
    const map = new Map();
    for (const el of allElements) map.set(el.id, el);
    return map;
  }, [allElements]);

  const effectiveSelectedElementsFull = useMemo(() => {
    const all = [];
    const added = new Set();
    for (const el of allElements) {
      if (screensFullSet.has(el.screen_id)) continue;
      if (selectedSet.has(el.id) || screensAllSet.has(el.screen_id)) {
        if (added.has(el.id)) continue;
        all.push(el);
        added.add(el.id);
      }
    }
    return all;
  }, [allElements, selectedSet, screensAllSet, screensFullSet]);

  const effectiveSelectedScreensFull = useMemo(() => {
    return screens.filter((s) => screensFullSet.has(s.id));
  }, [screens, screensFullSet]);

  const fullExcludeElements = useMemo(() => {
    const out = [];
    for (const screen of screens) {
      if (!screensFullSet.has(screen.id)) continue;
      const excludeSet = screensFullExcludeSets.get(screen.id);
      if (!excludeSet || excludeSet.size === 0) continue;
      const elements = screen.elements || [];
      for (const el of elements) {
        if (!el.is_valid) continue;
        const selector = getExcludeSelector({ ...el, _meta: parseMetadata(el.metadata) });
        if (excludeSet.has(selector)) {
          out.push({
            screen_id: screen.id,
            id: el.id,
            auto_name: el.auto_name,
            selector,
            screen_nome: screen.nome,
          });
        }
      }
    }
    return out;
  }, [screens, screensFullSet, screensFullExcludeSets]);

  const effectiveSelectedCount =
    effectiveSelectedElementsFull.length + effectiveSelectedScreensFull.length;

  const SIGN_TOKEN = "__SIGNATURE__";
  const SIGN_IMG_TOKEN = "__SIGNATURE_IMG__";
  const SIGN_TEXT_TOKEN = "__SIGNATURE_TEXT__";
  const SIGN_EXTRA_TOKEN = "__SIGNATURE_EXTRA__";

  const signaturePreview = useMemo(() => {
    if (!form.assinatura_img_base64 || !form.assinatura_img_mime) return "";
    return `data:${form.assinatura_img_mime};base64,${form.assinatura_img_base64}`;
  }, [form.assinatura_img_base64, form.assinatura_img_mime]);

  const signatureExtraPreview = useMemo(() => {
    if (!form.assinatura_extra_img_base64 || !form.assinatura_extra_img_mime) return "";
    return `data:${form.assinatura_extra_img_mime};base64,${form.assinatura_extra_img_base64}`;
  }, [form.assinatura_extra_img_base64, form.assinatura_extra_img_mime]);

  const signatureAssets = useMemo(() => {
    return assets.filter(
      (asset) =>
        asset.tipo === "signature" &&
        asset.ativo !== 0 &&
        asset.ativo !== "0" &&
        asset.ativo !== false
    );
  }, [assets]);

  const imageAssets = useMemo(() => {
    return assets.filter(
      (asset) =>
        (asset.tipo === "image" || !asset.tipo) &&
        asset.ativo !== 0 &&
        asset.ativo !== "0" &&
        asset.ativo !== false
    );
  }, [assets]);

  const previewContext = useMemo(() => {
    const now = new Date();
    return {
      data: now.toLocaleDateString("pt-BR"),
      hora: now.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }),
      dia_semana: now.toLocaleDateString("pt-BR", { weekday: "long" }),
    };
  }, []);

  const updatePickerDims = useCallback(() => {
    const img = pickerImgRef.current;
    if (!img || !img.naturalWidth) return;
    const rect = img.getBoundingClientRect();
    const scale = rect.width / img.naturalWidth;
    setPickerDims({
      naturalWidth: img.naturalWidth,
      naturalHeight: img.naturalHeight,
      renderWidth: rect.width,
      renderHeight: rect.height,
      scale: Number.isFinite(scale) && scale > 0 ? scale : 1,
    });
  }, []);

  const fetchPreviewImage = useCallback(async (url) => {
    const res = await fetch(url, { credentials: "include" });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `HTTP ${res.status}`);
    }
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  }, []);

  const refreshPickerImage = useCallback(async () => {
    if (!activeScreenId) return;
    setPickerError("");
    try {
      const url = await fetchPreviewImage(
        `${api.screens.previewUrl(activeScreenId, activeScreen?.url)}&t=${Date.now()}`
      );
      setPickerImgUrl((prev) => {
        if (prev && prev.startsWith("blob:")) URL.revokeObjectURL(prev);
        return url;
      });
    } catch (err) {
      setPickerImgUrl("");
      setPickerError("Nao foi possivel carregar a captura da tela. Tente escanear novamente.");
    }
  }, [activeScreenId, activeScreen?.url, fetchPreviewImage]);

  useEffect(() => {
    if (!activeScreenId) return;
    if (scanning) return;
    refreshPickerImage();
  }, [activeScreenId, scanning, refreshPickerImage]);

  useEffect(() => {
    if (!pickerImgUrl) return;
    const onResize = () => updatePickerDims();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [pickerImgUrl, updatePickerDims]);

  useEffect(() => {
    return () => {
      if (pickerImgUrl && pickerImgUrl.startsWith("blob:")) {
        URL.revokeObjectURL(pickerImgUrl);
      }
    };
  }, [pickerImgUrl]);

  function handlePickerImageLoad() {
    updatePickerDims();
  }

  function handlePickerImageError() {
    setPickerError("Nao foi possivel carregar a captura da tela. Tente escanear novamente.");
  }

  function handlePickerToggle(el) {
    toggleElement(el.id);
  }

  useEffect(() => {
    let cancelled = false;
    async function loadPreview() {
      if (!previewOpen || effectiveSelectedScreensFull.length === 0) return;
      const next = {};
      for (const screen of effectiveSelectedScreensFull) {
        next[screen.id] = { url: "", error: "", loading: true };
      }
      setPreviewScreens(next);
      for (const screen of effectiveSelectedScreensFull) {
        try {
          const url = await fetchPreviewImage(
            `${api.screens.previewUrl(
              screen.id,
              screen.url,
              screensFullExcludes[screen.id] || []
            )}&t=${Date.now()}`
          );
          if (cancelled) {
            URL.revokeObjectURL(url);
            continue;
          }
          setPreviewScreens((prev) => ({
            ...prev,
            [screen.id]: { url, error: "", loading: false },
          }));
        } catch (err) {
          if (cancelled) continue;
          setPreviewScreens((prev) => ({
            ...prev,
            [screen.id]: {
              url: "",
              error: err?.message || "Erro ao carregar",
              loading: false,
            },
          }));
        }
      }
    }
    loadPreview();
    return () => {
      cancelled = true;
    };
  }, [previewOpen, effectiveSelectedScreensFull, fetchPreviewImage]);

  useEffect(() => {
    const current = new Set();
    Object.values(previewScreens).forEach((item) => {
      if (item?.url && item.url.startsWith("blob:")) current.add(item.url);
    });
    for (const url of previewScreenUrlsRef.current) {
      if (!current.has(url)) URL.revokeObjectURL(url);
    }
    previewScreenUrlsRef.current = current;
  }, [previewScreens]);

  useEffect(() => {
    if (previewOpen) return;
    for (const url of previewScreenUrlsRef.current) {
      URL.revokeObjectURL(url);
    }
    previewScreenUrlsRef.current = new Set();
  }, [previewOpen]);

  const selectedTriggerSet = useMemo(() => {
    return new Set(selectedTriggers.map((t) => `${t.type}::${t.key}`));
  }, [selectedTriggers]);

  const triggerGroups = useMemo(() => {
    const groups = new Map();
    for (const item of triggerCatalog) {
      const group = item.group || "Outros";
      if (!groups.has(group)) groups.set(group, []);
      groups.get(group).push(item);
    }
    return Array.from(groups.entries());
  }, [triggerCatalog]);

  function applyPlaceholders(text, ctx) {
    return String(text || "")
      .replace(/\$\{(\w+)\}/g, (_, k) => (ctx[k] != null ? String(ctx[k]) : `\$\{${k}\}`))
      .replace(/\{\{(\w+)\}\}/g, (_, k) => (ctx[k] != null ? String(ctx[k]) : `{{${k}}}`));
  }

  function escapeHtml(s) {
    return String(s || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  }

  function markdownToPreviewHtml(md) {
    let src = escapeHtml(String(md || ""));
    src = src.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
    src = src.replace(/\*(.*?)\*/g, "<em>$1</em>");
    src = src.replace(/`([^`]+)`/g, "<code>$1</code>");
    src = src.replace(/^### (.*)$/gm, "<h3>$1</h3>");
    src = src.replace(/^## (.*)$/gm, "<h2>$1</h2>");
    src = src.replace(/^# (.*)$/gm, "<h1>$1</h1>");
    src = src.replace(/\n/g, "<br/>");
    return src;
  }

  function buildSignaturePreviewParts(ctx) {
    if (!form.assinatura_ativa) return { full: "", img: "", text: "", extra: "" };
    const textHtml = form.assinatura_md
      ? markdownToPreviewHtml(applyPlaceholders(form.assinatura_md, ctx))
      : "";
    const imgHtml = signaturePreview ? `<img src="${signaturePreview}" alt="Assinatura" />` : "";
    const extraHtml = signatureExtraPreview
      ? `<img src="${signatureExtraPreview}" alt="Assinatura extra" class="ae2-email-signature-extra" />`
      : "";
    if (!textHtml && !imgHtml && !extraHtml) return { full: "", img: "", text: "", extra: "" };
    const full = `
      <div class="ae2-email-signature">
        ${textHtml ? `<div class="ae2-email-text">${textHtml}</div>` : ""}
        ${imgHtml || ""}
        ${extraHtml || ""}
      </div>
    `;
    return { full, img: imgHtml, text: textHtml, extra: extraHtml };
  }

  function applySignaturePlaceholders(bodyHtml, parts) {
    const tokenized = String(bodyHtml || "")
      .replace(/\{\{\s*assinatura\s*\}\}/gi, SIGN_TOKEN)
      .replace(/\{\{\s*assinatura_img\s*\}\}/gi, SIGN_IMG_TOKEN)
      .replace(/\{\{\s*assinatura_texto\s*\}\}/gi, SIGN_TEXT_TOKEN)
      .replace(/\{\{\s*assinatura_text\s*\}\}/gi, SIGN_TEXT_TOKEN)
      .replace(/\{\{\s*assinatura_extra\s*\}\}/gi, SIGN_EXTRA_TOKEN);

    if (!parts.full) {
      return {
        html: tokenized
          .replaceAll(SIGN_TOKEN, "")
          .replaceAll(SIGN_IMG_TOKEN, "")
          .replaceAll(SIGN_TEXT_TOKEN, "")
          .replaceAll(SIGN_EXTRA_TOKEN, ""),
        placed: false,
      };
    }

    const hasToken =
      tokenized.includes(SIGN_TOKEN) ||
      tokenized.includes(SIGN_IMG_TOKEN) ||
      tokenized.includes(SIGN_TEXT_TOKEN) ||
      tokenized.includes(SIGN_EXTRA_TOKEN);

    if (!hasToken) return { html: tokenized, placed: false };

    return {
      html: tokenized
        .replaceAll(SIGN_TOKEN, parts.full)
        .replaceAll(SIGN_IMG_TOKEN, parts.img || "")
        .replaceAll(SIGN_TEXT_TOKEN, parts.text || "")
        .replaceAll(SIGN_EXTRA_TOKEN, parts.extra || ""),
      placed: true,
    };
  }

  const previewSignatureParts = useMemo(
    () => buildSignaturePreviewParts(previewContext),
    [form.assinatura_ativa, form.assinatura_md, signaturePreview, signatureExtraPreview, previewContext]
  );

  const previewBody = useMemo(() => {
    if (!form.corpo_md) return { html: "", placed: false };
    const raw = markdownToPreviewHtml(applyPlaceholders(form.corpo_md, previewContext));
    return applySignaturePlaceholders(raw, previewSignatureParts);
  }, [form.corpo_md, previewContext, previewSignatureParts]);

  function insertSignatureToken(token) {
    setForm((prev) => ({
      ...prev,
      corpo_md: prev.corpo_md ? `${prev.corpo_md}\n\n${token}` : token,
    }));
  }

  async function openPreview() {
    setPreviewError("");
    setPreviewOpen(true);
  }

  function selectAllActive() {
    if (screensAllSet.has(activeScreenId)) return;
    if (screensFullSet.has(activeScreenId)) {
      const selectors = activeElementsWithMeta
        .map((el) => getExcludeSelector(el))
        .filter(Boolean);
      setScreensFullExcludes((prev) => ({
        ...(prev || {}),
        [activeScreenId]: Array.from(new Set(selectors)),
      }));
      return;
    }
    const ids = activeElementsRaw.map((e) => e.id);
    if (ids.length === 0) return;
    setSelectedElements((prev) => Array.from(new Set([...prev, ...ids])));
  }

  function clearActive() {
    if (screensAllSet.has(activeScreenId)) return;
    if (screensFullSet.has(activeScreenId)) {
      setScreensFullExcludes((prev) => ({
        ...(prev || {}),
        [activeScreenId]: [],
      }));
      return;
    }
    const ids = new Set(activeElementsRaw.map((e) => e.id));
    setSelectedElements((prev) => prev.filter((id) => !ids.has(id)));
  }

  function handleSignatureFile(file) {
    if (!file) return;
    if (file.size > 512 * 1024) {
      alert("A imagem da assinatura deve ter ate 512 KB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const raw = String(reader.result || "");
      const match = raw.match(/^data:(.*?);base64,(.*)$/);
      if (!match) return;
      setForm((prev) => ({
        ...prev,
        assinatura_img_mime: match[1],
        assinatura_img_base64: match[2],
        assinatura_img_nome: file.name || "assinatura.png",
        assinatura_ativa: true,
      }));
    };
    reader.readAsDataURL(file);
  }

  function handleSignatureExtraFile(file) {
    if (!file) return;
    if (file.size > 512 * 1024) {
      alert("A imagem extra da assinatura deve ter ate 512 KB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const raw = String(reader.result || "");
      const match = raw.match(/^data:(.*?);base64,(.*)$/);
      if (!match) return;
      setForm((prev) => ({
        ...prev,
        assinatura_extra_img_mime: match[1],
        assinatura_extra_img_base64: match[2],
        assinatura_extra_img_nome: file.name || "assinatura-extra.png",
        assinatura_ativa: true,
      }));
    };
    reader.readAsDataURL(file);
  }

  function applyAssetToSignature(asset) {
    if (!asset?.img_base64 || !asset?.img_mime) return;
    setForm((prev) => ({
      ...prev,
      assinatura_img_base64: asset.img_base64,
      assinatura_img_mime: asset.img_mime,
      assinatura_img_nome: asset.img_nome || asset.nome || "assinatura.png",
      assinatura_ativa: true,
    }));
  }

  function applyAssetToExtra(asset) {
    if (!asset?.img_base64 || !asset?.img_mime) return;
    setForm((prev) => ({
      ...prev,
      assinatura_extra_img_base64: asset.img_base64,
      assinatura_extra_img_mime: asset.img_mime,
      assinatura_extra_img_nome: asset.img_nome || asset.nome || "assinatura-extra.png",
      assinatura_ativa: true,
    }));
  }

  async function handleSave() {
    if (!form.nome) return alert("Nome da automação é obrigatório");
    if (!form.assunto) return alert("Assunto do e-mail e obrigatorio");
    if (form.destinatarios_ids.length === 0) return alert("Selecione pelo menos um destinatário");
    if (selectedElements.length === 0 && screensAll.length === 0 && screensFull.length === 0) {
      return alert("Selecione pelo menos um elemento, inclua uma tela completa ou ative 'Incluir tudo' em alguma tela");
    }

    setSaving(true);
    try {
      const manualElementIds = selectedElements.filter((id) => {
        const el = elementById.get(id);
        return el ? !screensAllSet.has(el.screen_id) && !screensFullSet.has(el.screen_id) : true;
      });

        const payload = {
          ...form,
          dias_semana: form.dias_semana.join(","),
          element_ids: manualElementIds,
          screens_all_ids: screensAll,
          screens_full_ids: screensFull,
          screens_full_excludes: screensFullExcludes,
          triggers: selectedTriggers,
        };

      if (isEditing) {
        await api.automations.update(id, payload);
      } else {
        await api.automations.create(payload);
      }

      nav("/automacao-emails/automations");
    } catch (err) {
      alert(`Erro ao salvar: ${err?.message}`);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="ae2-page">
        <div className="ae2-loading">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="ae2-page">

      <div className="ae2-steps">
        <div className={`ae2-step ${step >= 1 ? "active" : ""} ${step > 1 ? "done" : ""}`}>
          <span className="ae2-step-number">{step > 1 ? ICONS.check : "1"}</span>
          <span>Selecionar capturas</span>
        </div>
        <div className="ae2-step-line" />
        <div className={`ae2-step ${step >= 2 ? "active" : ""} ${step > 2 ? "done" : ""}`}>
          <span className="ae2-step-number">{step > 2 ? ICONS.check : "2"}</span>
          <span>Compor e-mail</span>
        </div>
        <div className="ae2-step-line" />
        <div className={`ae2-step ${step >= 3 ? "active" : ""}`}>
          <span className="ae2-step-number">3</span>
          <span>Agendar envio</span>
        </div>
      </div>

      <div className="ae2-wizard-footer ae2-wizard-footer-top">
        <button
          className="ae2-btn ae2-btn-ghost"
          onClick={() => (step === 1 ? nav("/automacao-emails") : setStep(step - 1))}
        >
          {ICONS.arrowLeft}
          <span>{step === 1 ? "Cancelar" : "Voltar"}</span>
        </button>

        {step < 3 ? (
          <div style={{ display: "flex", gap: 10 }}>
            {step >= 2 && (
              <button className="ae2-btn ae2-btn-ghost" onClick={openPreview}>
                Ver preview do e-mail
              </button>
            )}
            <button
              className="ae2-btn ae2-btn-primary"
              onClick={() => setStep(step + 1)}
              disabled={
                step === 1 &&
                selectedElements.length === 0 &&
                screensAll.length === 0 &&
                screensFull.length === 0
              }
            >
              <span>Proximo</span>
              {ICONS.arrowRight}
            </button>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 10 }}>
            <button className="ae2-btn ae2-btn-ghost" onClick={openPreview}>
              Ver preview do e-mail
            </button>
            <button className="ae2-btn ae2-btn-success" onClick={handleSave} disabled={saving}>
              {saving ? ICONS.loader : ICONS.check}
              <span>{saving ? "Salvando..." : "Salvar automação"}</span>
            </button>
          </div>
        )}
      </div>

      {step === 1 && (
        <div className="ae2-step-body">
          <div className="ae2-step-header">
            <div>
              <h3>Capturas</h3>
              <p>Escolha a tela e edite o print no preview.</p>
            </div>
          </div>

          {screens.length === 0 ? (
            <div className="ae2-empty">
              <p>Nenhuma tela cadastrada. Cadastre uma tela primeiro.</p>
              <button className="ae2-btn ae2-btn-primary" onClick={() => nav("/automacao-emails/screens")}> 
                Ir para Telas
              </button>
            </div>
          ) : (
            <div className="ae2-select-grid">
              <div className="ae2-panel">
                <div className="ae2-panel-header">
                  <div>
                    <h4>Telas do sistema</h4>
                    <span className="ae2-muted">Selecione uma tela para iniciar a captura.</span>
                  </div>
                </div>
                <div className="ae2-field">
                  <input
                    type="text"
                    placeholder="Buscar tela..."
                    value={screenSearch}
                    onChange={(e) => setScreenSearch(e.target.value)}
                  />
                </div>
                <div className="ae2-screen-list">
                    {filteredScreens.map((screen) => {
                      const excludeCount = screensFullExcludeSets.get(screen.id)?.size || 0;
                      return (
                        <button
                          key={screen.id}
                          type="button"
                          className={`ae2-screen-item ${activeScreenId === screen.id ? "active" : ""}`}
                          onClick={() => {
                            setActiveScreenId(screen.id);
                            if (!screensFullSet.has(screen.id)) {
                              setScreensFull((prev) => Array.from(new Set([...prev, screen.id])));
                              setScreensAll((prev) => prev.filter((id) => id !== screen.id));
                              const ids = (screen.elements || []).map((e) => e.id);
                              if (ids.length > 0) {
                                setSelectedElements((prev) => prev.filter((id) => !ids.includes(id)));
                              }
                            }
                          }}
                        >
                        <div>
                          <div className="ae2-screen-title">{screen.nome}</div>
                        </div>
                        <div className="ae2-screen-meta">
                          {excludeCount > 0 && (
                            <span className="ae2-badge warning">Excluídos: {excludeCount}</span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                  {filteredScreens.length === 0 && (
                    <div className="ae2-empty-inline">Nenhuma tela encontrada.</div>
                  )}
                </div>
              </div>

              <div className="ae2-panel">
                <div className="ae2-panel-header">
                  <div>
                    <h4>{activeScreen?.nome || "Selecione uma tela"}</h4>
                    {activeScreen?.url && <span className="ae2-muted">{activeScreen.url}</span>}
                  </div>
                </div>

                {activeScreen ? (
                  <div className="ae2-screen-preview-block">
                    {!scanning && pickerError && (
                      <div className="ae2-info error">{pickerError}</div>
                    )}
                    <div className="ae2-picker-layout ae2-picker-layout-inline">
                      <div className="ae2-picker-preview">
                        {scanning === activeScreen.id && (
                          <div className="ae2-picker-loading-center" aria-live="polite">
                            {ICONS.loader}
                          </div>
                        )}
                        <div className="ae2-picker-toolbar">
                          <span className="ae2-muted">Clique nos blocos para excluir do print.</span>
                        </div>
                        <div className="ae2-picker-scroll">
                          <div className="ae2-picker-canvas">
                            {pickerImgUrl ? (
                              <img
                                ref={pickerImgRef}
                                src={pickerImgUrl}
                                alt={activeScreen?.nome || "Preview"}
                                onLoad={handlePickerImageLoad}
                                onError={handlePickerImageError}
                              />
                            ) : (
                              <div className="ae2-empty-inline">Preview será exibido aqui.</div>
                            )}
                            {!scanning && pickerImgUrl && (
                              <div className="ae2-picker-overlay">
                                {pickerElements.map((el) => {
                                  const rect = el._meta?.rect || {};
                                  const scale = pickerDims.scale || 1;
                                  const x = toNumber(rect.x ?? rect.left) ?? 0;
                                  const y = toNumber(rect.y ?? rect.top) ?? 0;
                                  const width = toNumber(rect.width) ?? 0;
                                  const height = toNumber(rect.height) ?? 0;
                                  const selector = getExcludeSelector(el);
                                  const isExcluded = screensFullSet.has(activeScreenId)
                                    ? activeFullExcludeSet.has(selector)
                                    : false;
                                  const style = {
                                    left: x * scale,
                                    top: y * scale,
                                    width: width * scale,
                                    height: height * scale,
                                  };
                                  const isSelected = screensFullSet.has(activeScreenId)
                                    ? isExcluded
                                    : selectedSet.has(el.id);
                                  return (
                                    <button
                                      key={el.id}
                                      type="button"
                                      className={`ae2-picker-box ${isSelected ? "selected" : ""}`}
                                      style={style}
                                      title="Clique para excluir"
                                      onClick={() => handlePickerToggle(el)}
                                    >
                                      {isSelected && <span className="ae2-picker-box-label">X</span>}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                        {pickerElements.length === 0 && (
                          <div className="ae2-empty-inline">
                            Nenhum bloco detectado. Você ainda pode ver o preview completo.
                          </div>
                        )}
                        <div className="ae2-picker-hint">
                          Excluídos: {pickerSelected.length} elemento(s).
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="ae2-picker-empty">
                    <div className="ae2-picker-empty-title">Selecione uma tela</div>
                    <div className="ae2-muted">
                      Escolha na lista ao lado para ver o preview.
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      )}

      {step === 2 && (
        <div className="ae2-step-body">
          <div className="ae2-form-grid">
            <div className="ae2-form-col">
              <div className="ae2-field">
                <label>Nome da automação *</label>
                <input
                  type="text"
                  value={form.nome}
                  onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  placeholder="Ex: Relatorio Diario de Vendas"
                />
              </div>

              <div className="ae2-field">
                <label>Assunto do e-mail *</label>
                <input
                  type="text"
                  value={form.assunto}
                  onChange={(e) => setForm({ ...form, assunto: e.target.value })}
                  placeholder="Ex: Vendas do Dia - {{data}}"
                />
                <span className="ae2-hint">
                  Use {"{{data}}"}, {"{{hora}}"}, {"{{dia_semana}}"}
                </span>
              </div>

              <div className="ae2-field">
                <label>Corpo do e-mail (Markdown)</label>
                <textarea
                  value={form.corpo_md}
                  onChange={(e) => setForm({ ...form, corpo_md: e.target.value })}
                  placeholder="Ola equipe,\n\nSegue o relatorio automatico com os dados atualizados."
                  rows={6}
                />
              </div>

              <div className="ae2-field">
                <label>Assinatura</label>
                <div className="ae2-tabbar">
                  <button
                    type="button"
                    className={`ae2-tab ${signatureTab === "assinatura" ? "active" : ""}`}
                    onClick={() => setSignatureTab("assinatura")}
                  >
                    Assinatura
                  </button>
                  <button
                    type="button"
                    className={`ae2-tab ${signatureTab === "extra" ? "active" : ""}`}
                    onClick={() => setSignatureTab("extra")}
                  >
                    Imagem extra
                  </button>
                </div>

                {signatureTab === "assinatura" && (
                  <div className="ae2-signature-panel">
                    <textarea
                      value={form.assinatura_md}
                      onChange={(e) => setForm({ ...form, assinatura_md: e.target.value })}
                      placeholder="Atenciosamente,\nEquipe"
                      rows={4}
                    />
                    <div className="ae2-signature">
                      <div className="ae2-upload">
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/jpg"
                          onChange={(e) => handleSignatureFile(e.target.files?.[0])}
                        />
                        <span className="ae2-muted">Imagem de assinatura (PNG/JPG, ate 512 KB)</span>
                      </div>
                      {signaturePreview && (
                        <div className="ae2-signature-preview">
                          <img src={signaturePreview} alt="Assinatura" />
                          <button
                            type="button"
                            className="ae2-btn ae2-btn-ghost"
                            onClick={() =>
                              setForm((prev) => ({
                                ...prev,
                                assinatura_img_base64: "",
                                assinatura_img_mime: "",
                                assinatura_img_nome: "",
                              }))
                            }
                          >
                            Remover imagem
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="ae2-asset-picker">
                      <div className="ae2-asset-picker-header">
                        <strong>Biblioteca de assinaturas</strong>
                        <button
                          type="button"
                          className="ae2-btn ae2-btn-ghost"
                          onClick={() => nav("/automacao-emails/assets")}
                        >
                          Gerenciar
                        </button>
                      </div>
                      {signatureAssets.length === 0 ? (
                        <div className="ae2-muted">
                          Nenhuma assinatura salva.
                        </div>
                      ) : (
                        <div className="ae2-asset-picker-grid">
                          {signatureAssets.map((asset) => (
                            <button
                              key={asset.id}
                              type="button"
                              className="ae2-asset-option"
                              onClick={() => applyAssetToSignature(asset)}
                            >
                              <img
                                src={`data:${asset.img_mime};base64,${asset.img_base64}`}
                                alt={asset.nome || asset.img_nome || "Assinatura"}
                              />
                              <span>{asset.nome || asset.img_nome || "Assinatura"}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {signatureTab === "extra" && (
                  <div className="ae2-signature-panel">
                    <div className="ae2-signature">
                      <div className="ae2-upload">
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/jpg"
                          onChange={(e) => handleSignatureExtraFile(e.target.files?.[0])}
                        />
                        <span className="ae2-muted">Imagem extra (PNG/JPG, ate 512 KB)</span>
                      </div>
                      {signatureExtraPreview && (
                        <div className="ae2-signature-preview">
                          <img src={signatureExtraPreview} alt="Assinatura extra" />
                          <button
                            type="button"
                            className="ae2-btn ae2-btn-ghost"
                            onClick={() =>
                              setForm((prev) => ({
                                ...prev,
                                assinatura_extra_img_base64: "",
                                assinatura_extra_img_mime: "",
                                assinatura_extra_img_nome: "",
                              }))
                            }
                          >
                            Remover imagem
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="ae2-asset-picker">
                      <div className="ae2-asset-picker-header">
                        <strong>Biblioteca de imagens</strong>
                        <button
                          type="button"
                          className="ae2-btn ae2-btn-ghost"
                          onClick={() => nav("/automacao-emails/assets")}
                        >
                          Gerenciar
                        </button>
                      </div>
                      {imageAssets.length === 0 ? (
                        <div className="ae2-muted">
                          Nenhuma imagem salva.
                        </div>
                      ) : (
                        <div className="ae2-asset-picker-grid">
                          {imageAssets.map((asset) => (
                            <button
                              key={asset.id}
                              type="button"
                              className="ae2-asset-option"
                              onClick={() => applyAssetToExtra(asset)}
                            >
                              <img
                                src={`data:${asset.img_mime};base64,${asset.img_base64}`}
                                alt={asset.nome || asset.img_nome || "Imagem"}
                              />
                              <span>{asset.nome || asset.img_nome || "Imagem"}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <div className="ae2-signature-actions">
                  <span className="ae2-muted">
                    Para posicionar a assinatura no corpo, use:
                    <strong>{"{{assinatura}}"}</strong>, <strong>{"{{assinatura_img}}"}</strong>,
                    <strong>{"{{assinatura_texto}}"}</strong> ou <strong>{"{{assinatura_extra}}"}</strong>
                  </span>
                  <div className="ae2-inline-actions">
                    <button
                      type="button"
                      className="ae2-btn ae2-btn-ghost"
                      onClick={() => insertSignatureToken("{{assinatura}}")}
                    >
                      Inserir {"{{assinatura}}"}
                    </button>
                    <button
                      type="button"
                      className="ae2-btn ae2-btn-ghost"
                      onClick={() => insertSignatureToken("{{assinatura_img}}")}
                    >
                      Inserir {"{{assinatura_img}}"}
                    </button>
                    <button
                      type="button"
                      className="ae2-btn ae2-btn-ghost"
                      onClick={() => insertSignatureToken("{{assinatura_texto}}")}
                    >
                      Inserir {"{{assinatura_texto}}"}
                    </button>
                    <button
                      type="button"
                      className="ae2-btn ae2-btn-ghost"
                      onClick={() => insertSignatureToken("{{assinatura_extra}}")}
                    >
                      Inserir {"{{assinatura_extra}}"}
                    </button>
                  </div>
                </div>
                <label className="ae2-check">
                  <input
                    type="checkbox"
                    checked={form.assinatura_ativa}
                    onChange={(e) => setForm({ ...form, assinatura_ativa: e.target.checked })}
                  />
                  <span>Incluir assinatura no final do e-mail</span>
                </label>
              </div>
            </div>

            <div className="ae2-form-col">
              <div className="ae2-card ae2-side-card">
                <h4>Destinatários</h4>
                <div className="ae2-recipient-filters">
                  <input
                    type="text"
                    value={recipientSearch}
                    onChange={(e) => setRecipientSearch(e.target.value)}
                    placeholder="Buscar destinatário..."
                  />
                  <select
                    value={recipientGroup}
                    onChange={(e) => setRecipientGroup(e.target.value)}
                  >
                    <option value="all">Todos os grupos</option>
                    {recipientGroups.map((grupo) => (
                      <option key={grupo} value={grupo}>{grupo}</option>
                    ))}
                  </select>
                </div>
                {filteredRecipients.length === 0 ? (
                  <div className="ae2-empty-inline">Nenhum destinatário para o filtro selecionado.</div>
                ) : (
                  <div className="ae2-recipient-grid">
                    {filteredRecipients.map((email) => {
                      const groups = getEmailGroups(email);
                      return (
                        <button
                          key={email.id}
                          type="button"
                          className={`ae2-recipient ${form.destinatarios_ids.includes(email.id) ? "selected" : ""}`}
                          onClick={() => toggleDestinatario(email.id)}
                        >
                          <div className="ae2-recipient-info">
                            <span className="ae2-recipient-main">{email.nome || email.email}</span>
                            <div className="ae2-recipient-meta">
                              <small>{email.email}</small>
                              <div className="ae2-recipient-badges">
                                {groups.length ? groups.map((grupo) => (
                                  <span key={`${email.id}-${grupo}`} className="ae2-recipient-badge">{grupo}</span>
                                )) : <span className="ae2-recipient-badge">Sem grupo</span>}
                              </div>
                            </div>
                          </div>
                          {form.destinatarios_ids.includes(email.id) && ICONS.check}
                        </button>
                      );
                    })}
                  </div>
                )}
                <div className="ae2-hint">{form.destinatarios_ids.length} destinatário(s) selecionado(s)</div>
              </div>

              <div className="ae2-card ae2-side-card">
                <h4>Capturas selecionadas</h4>
                {effectiveSelectedElementsFull.length === 0 && effectiveSelectedScreensFull.length === 0 ? (
                  <p className="ae2-muted">Nenhuma captura selecionada.</p>
                ) : (
                  <>
                    {effectiveSelectedScreensFull.length > 0 && (
                      <div className="ae2-selected-block">
                        <strong>Telas inteiras</strong>
                        <ul className="ae2-selected-summary">
                          {effectiveSelectedScreensFull.map((s) => (
                            <li key={`full-${s.id}`}>
                              <span>{s.nome}</span>
                              <small>{s.url}</small>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {effectiveSelectedElementsFull.length > 0 && (
                      <div className="ae2-selected-block">
                        <strong>Elementos</strong>
                        <ul className="ae2-selected-summary">
                          {effectiveSelectedElementsFull.map((el) => (
                            <li key={el.id}>
                              <span>{el.auto_name}</span>
                              <small>{el.screen_nome}</small>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="ae2-step-body">
          <div className="ae2-form-grid">
            <div className="ae2-form-col">
              <div className="ae2-field">
                <label>Frequencia</label>
                <div className="ae2-radio-group">
                  {FREQ_OPTIONS.map((opt) => (
                    <label key={opt.value} className="ae2-radio">
                      <input
                        type="radio"
                        name="freq"
                        value={opt.value}
                        checked={form.freq === opt.value}
                        onChange={() => setForm({ ...form, freq: opt.value })}
                      />
                      <span>{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="ae2-field">
                <label>Horario</label>
                <input
                  type="time"
                  value={form.hora}
                  onChange={(e) => setForm({ ...form, hora: e.target.value })}
                />
              </div>

              {form.freq === "WEEKLY" && (
                <div className="ae2-field">
                  <label>Dias da semana</label>
                  <div className="ae2-days">
                    {DAYS_OPTIONS.map((day) => (
                      <button
                        key={day.value}
                        type="button"
                        className={`ae2-day ${form.dias_semana.includes(day.value) ? "selected" : ""}`}
                        onClick={() => toggleDiaSemana(day.value)}
                      >
                        {day.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {form.freq === "MONTHLY" && (
                <div className="ae2-field">
                  <label>Dia do mes</label>
                  <input
                    type="number"
                    min="1"
                    max="31"
                    value={form.dia_mes}
                    onChange={(e) => setForm({ ...form, dia_mes: parseInt(e.target.value) || 1 })}
                  />
                </div>
              )}

              <label className="ae2-check">
                <input
                  type="checkbox"
                  checked={form.attach_pdf}
                  onChange={(e) => setForm({ ...form, attach_pdf: e.target.checked })}
                />
                <span>Anexar versao em PDF</span>
              </label>

              <div className="ae2-card ae2-side-card">
                <h4>Gatilhos do sistema</h4>
                <p className="ae2-muted">
                  Opcional. Quando um gatilho ocorrer, o e-mail sera enviado imediatamente.
                </p>
                {triggerGroups.length === 0 ? (
                  <div className="ae2-muted">Nenhum gatilho disponivel.</div>
                ) : (
                  triggerGroups.map(([group, items]) => (
                    <div key={group} className="ae2-trigger-group">
                      <div className="ae2-trigger-title">{group}</div>
                      <div className="ae2-trigger-list">
                        {items.map((item) => {
                          const key = item.key || item.trigger_key;
                          const type = item.type || item.trigger_type;
                          const checked = selectedTriggerSet.has(`${type}::${key}`);
                          return (
                            <label key={`${type}-${key}`} className="ae2-check ae2-trigger">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleTrigger(item)}
                              />
                              <span>{item.label || key}</span>
                              <small>{key}</small>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="ae2-form-col">
              <div className="ae2-card ae2-side-card">
                <h4>Resumo da automação</h4>
                <div className="ae2-summary">
                  <div><strong>Nome:</strong> {form.nome || "(nao definido)"}</div>
                  <div><strong>Assunto:</strong> {form.assunto || "(nao definido)"}</div>
                  <div>
                    <strong>Capturas:</strong>{" "}
                    {effectiveSelectedElementsFull.length} elemento(s), {effectiveSelectedScreensFull.length} tela(s)
                  </div>
                  <div><strong>Destinatários:</strong> {form.destinatarios_ids.length} selecionado(s)</div>
                  <div><strong>Assinatura:</strong> {form.assinatura_ativa ? "Sim" : "Nao"}</div>
                  <div><strong>Gatilhos:</strong> {selectedTriggers.length} selecionado(s)</div>
                  <div>
                    <strong>Agendamento:</strong> {form.freq} as {form.hora}
                    {form.freq === "WEEKLY" && ` (${form.dias_semana.join(", ")})`}
                    {form.freq === "MONTHLY" && ` (dia ${form.dia_mes})`}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {previewOpen && (
        <div className="ae2-modal-overlay" onClick={() => setPreviewOpen(false)}>
          <div className="ae2-modal ae2-modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="ae2-modal-header">
              <div>
                <h2>Preview do e-mail</h2>
                <div className="ae2-modal-sub">Visual aproximado antes do envio real</div>
              </div>
              <button className="ae2-btn ae2-btn-glass" onClick={() => setPreviewOpen(false)}>
                {ICONS.x}
              </button>
            </div>
            <div className="ae2-modal-body">
              {previewError && <div className="ae2-info error">{previewError}</div>}
              <div className="ae2-email-preview">
                <div className="ae2-email-header">
                  <div className="ae2-email-subject">
                    {applyPlaceholders(form.assunto || "(sem assunto)", previewContext)}
                  </div>
                  <div className="ae2-email-meta">
                    Gerado automaticamente em {previewContext.data} as {previewContext.hora}
                  </div>
                </div>

                  <div className="ae2-email-body">
                    {form.corpo_md ? (
                      <div
                        className="ae2-email-text"
                        dangerouslySetInnerHTML={{
                          __html: previewBody.html,
                        }}
                      />
                    ) : (
                      <div className="ae2-muted">Sem corpo de mensagem.</div>
                    )}

                  {effectiveSelectedScreensFull.length > 0 && (
                    <div className="ae2-email-section">
                      <h4>Telas completas</h4>
                  {effectiveSelectedScreensFull.map((screen) => {
                    const preview = previewScreens[screen.id];
                    return (
                      <div key={screen.id} className="ae2-email-element">
                        <div className="ae2-email-element-title">
                          {screen.nome} <span>(Tela inteira)</span>
                        </div>
                        <div className="ae2-email-element-image">
                          {preview?.loading ? (
                            <div className="ae2-muted">Carregando preview...</div>
                          ) : preview?.url ? (
                            <img src={preview.url} alt={screen.nome} />
                          ) : (
                            <div className="ae2-muted">
                              Preview indisponivel. {preview?.error || ""}
                            </div>
                          )}
                        </div>
                        <div className="ae2-email-element-meta">Captura full page</div>
                      </div>
                    );
                  })}
                    </div>
                  )}

                  {effectiveSelectedElementsFull.length > 0 && (
                    <div className="ae2-email-section">
                      <h4>Dados capturados</h4>
                      {effectiveSelectedElementsFull.map((el) => (
                        <div key={el.id} className="ae2-email-element">
                          <div className="ae2-email-element-title">
                            {el.auto_name} <span>({el.screen_nome})</span>
                          </div>
                          <div className="ae2-email-element-image">
                            <img
                              src={`${api.elements.previewUrl(el.id, "image")}&t=${Date.now()}`}
                              alt={el.auto_name}
                            />
                          </div>
                          <div className="ae2-email-element-meta">{el.dimensions || "-"}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {!previewBody.placed &&
                    form.assinatura_ativa &&
                    (form.assinatura_md || signaturePreview || signatureExtraPreview) && (
                    <div className="ae2-email-signature">
                      {form.assinatura_md && (
                        <div
                          className="ae2-email-text"
                          dangerouslySetInnerHTML={{
                            __html: markdownToPreviewHtml(
                              applyPlaceholders(form.assinatura_md, previewContext)
                            ),
                          }}
                        />
                      )}
                      {signaturePreview && <img src={signaturePreview} alt="Assinatura" />}
                      {signatureExtraPreview && (
                        <img
                          src={signatureExtraPreview}
                          alt="Assinatura extra"
                          className="ae2-email-signature-extra"
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="ae2-modal-footer">
              <button className="ae2-btn ae2-btn-ghost" onClick={() => setPreviewOpen(false)}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
